import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Activity } from '@core/models';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { AsyncPipe } from '@angular/common';
import { TableUserRendererComponent } from 'tsui/lib/ts-table/renderer-components/table-user-renderer/table-user-renderer.component';
import moment from 'moment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ICellRendererAngularComp } from 'ag-grid-angular';

@Component({
  selector: 'audit-list',
  standalone: true,
  imports: [TsTableComponent, AsyncPipe],
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditListComponent implements OnInit {
  @Input() activities: Activity[];
  @Input() loading: boolean;

  colDefs: ColumnDef[] = [];
  gridOptions = {
    rowHeight: 56,
  };

  ngOnInit(): void {
    this.colDefs = [
      {
        tsTableOptions: {
          field: 'related_to_type',
          headerName: 'Description',
          sortable: false,
          resizable: true,
          editable: false,
          minWidth: 800,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => this.getActivitySummary(params.data),
          cellRenderer: SafeHtmlCellRendererComponent,
        },
        type: 'text',
        label: 'Description',
        name: 'description',
      },
      {
        tsTableOptions: {
          field: 'action',
          headerName: 'Action',
          sortable: false,
          resizable: false,
          editable: false,
          maxWidth: 800,
          suppressHeaderMenuButton: true,
        },
        type: 'text',
        label: 'Action',
        name: 'action',
      },
      {
        tsTableOptions: {
          field: 'old_val',
          headerName: 'Old Value',
          sortable: false,
          editable: false,
          maxWidth: 800,
          resizable: false,
          tooltipValueGetter: (params) => {
            return params.value;
          },
          suppressHeaderMenuButton: true,
        },
        type: 'text',
        label: 'Old Value',
        name: 'old_val',
      },
      {
        tsTableOptions: {
          field: 'new_val',
          headerName: 'New Value',
          sortable: false,
          editable: false,
          maxWidth: 800,
          resizable: false,
          tooltipValueGetter: (params) => {
            return params.value;
          },
          suppressHeaderMenuButton: true,
        },
        type: 'text',
        label: 'New Value',
        name: 'new_val',
      },
      {
        tsTableOptions: {
          field: 'user_name',
          headerName: 'User',
          sortable: false,
          editable: false,
          maxWidth: 800,
          resizable: false,
          suppressHeaderMenuButton: true,
          cellRenderer: TableUserRendererComponent,
        },
        type: 'text',
        label: 'User',
        name: 'user',
      },
      {
        tsTableOptions: {
          field: 'created_at',
          headerName: 'Created At',
          sortable: false,
          maxWidth: 800,
          resizable: false,
          suppressHeaderMenuButton: true,
          valueFormatter: (params) => {
            return params.value ? moment(params.value).format('MMMM D, YYYY') : '';
          },
        },
        type: 'date',
        label: 'Created At',
        name: 'Created At',
      },
    ];
  }

  private getActivitySummary(activity: Activity): string {
    if (activity.summary) {
      return activity.summary;
    }
    const user = activity.user_name || '';
    const actionVerb =
      activity.action === 'update'
        ? 'updated'
        : activity.action === 'create'
          ? 'created'
          : activity.action === 'destroy'
            ? 'deleted'
            : activity.action;
    const field = activity.field_label || activity.field || '';
    const newVal = activity.new_val || '';
    const oldVal = activity.old_val || '';
    const title = activity.project_title || 'null';
    return `${user} ${actionVerb} ${field} to ${newVal}, it was ${oldVal} on ${title}`;
  }
}

@Component({
  selector: 'safe-html-cell-renderer',
  template: `<span [innerHTML]="safeHtml"></span>`,
})
export class SafeHtmlCellRendererComponent implements ICellRendererAngularComp {
  public safeHtml: SafeHtml;
  params;

  constructor(private sanitizer: DomSanitizer) {}

  agInit(params: any): void {
    this.params = params;
    this.refresh(params);
  }

  refresh(params: any): boolean {
    this.params = params;
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(params.value);
    return true;
  }
}
